import * as React from 'react';

import { Correctness } from '@showbie-socrative/socrative-utils';
import cx from 'classnames';

import { Box } from '~shared/components/Box';
import { FocusIndicator } from '~shared/components/FocusIndicator/FocusIndicator';
import {
  IconCheckmark,
  IconDash,
  IconStroke,
  IconX,
  IconResultsGridPencil,
} from '~shared/components/icons/micro';
import { StudentScore } from '~teacher/models/activities';

import { calculateEffectiveCorrectness } from '../StudentScore/utils';
import { ResultsCellDogEar } from './ResultsCellDogEar';
import { StudentResponse } from './ResultsTable';

enum ResponseState {
  Unanswered = 'unanswered',
  Answered = 'answered',
  Correct = 'correct',
  Incorrect = 'incorrect',
  Partial = 'partial',
}

type ResponseCellClass = {
  background: string;
  text: string;
  fold: string;
  hover: string;
};

const ResponseStateClassMap: {
  [key in ResponseState]: ResponseCellClass;
} = {
  [ResponseState.Unanswered]: {
    background: 'bg-grey-50',
    text: 'text-blue-900',
    fold: '',
    hover: '',
  },
  [ResponseState.Answered]: {
    background: 'bg-grey-300',
    text: 'text-blue-900',
    fold: 'fill-grey-500 group-hocus:fill-grey-600',
    hover: 'hover:bg-grey-400',
  },
  [ResponseState.Correct]: {
    background: 'bg-green-200',
    text: 'text-green-900',
    fold: 'fill-green-300 group-hocus:fill-green-500',
    hover: 'hover:bg-green-300',
  },
  [ResponseState.Partial]: {
    background: 'bg-amber-100',
    text: 'text-amber-900',
    fold: 'fill-amber-300 group-hocus:fill-amber-400',
    hover: 'hover:bg-amber-200',
  },
  [ResponseState.Incorrect]: {
    background: 'bg-red-300',
    text: 'text-red-900',
    fold: 'fill-red-400 group-hocus:fill-red-500',
    hover: 'hover:bg-red-400',
  },
};

const propsToResponseClass = (
  correctness: null | Correctness,
  showResults: boolean
): ResponseCellClass => {
  switch (correctness) {
    case null:
      return ResponseStateClassMap[ResponseState.Answered];
    case Correctness.Correct:
      return showResults
        ? ResponseStateClassMap[ResponseState.Correct]
        : ResponseStateClassMap[ResponseState.Answered];
    case Correctness.Partial:
      return showResults
        ? ResponseStateClassMap[ResponseState.Partial]
        : ResponseStateClassMap[ResponseState.Answered];
    case Correctness.Incorrect:
      return showResults
        ? ResponseStateClassMap[ResponseState.Incorrect]
        : ResponseStateClassMap[ResponseState.Answered];
    case undefined:
    default:
      return ResponseStateClassMap[ResponseState.Unanswered];
  }
};

interface ResponseIconProps {
  correctness: null | Correctness;
}

const ResponseIcon = ({ correctness }: ResponseIconProps) => {
  switch (correctness) {
    case null:
      return <IconDash />;
    case Correctness.Correct:
      return <IconCheckmark />;
    case Correctness.Partial:
      return <IconStroke />;
    case Correctness.Incorrect:
      return <IconX />;
    case undefined:
    default:
      return null;
  }
};

interface CellProps {
  response: StudentResponse;
  studentScore?: StudentScore;
  gradingWeight?: number;
  canEditResponse: boolean;
  onSelectResponse?: (e) => void;
  showResults: boolean;
  showResponses: boolean;
  isShortAnswer: boolean;
}

/**
 * Displays a table cell for a participant's response to a question.
 */
export function ResultsResponseCell({
  response,
  studentScore,
  gradingWeight,
  canEditResponse,
  onSelectResponse,
  showResults,
  showResponses,
  isShortAnswer = false,
}: CellProps): React.ReactElement {
  const { answerText, isCorrect } = response;
  const hasGradeOverride = !!studentScore;

  

  let correctnessValue = null;

  if (isCorrect === undefined) {
    correctnessValue = undefined;
  } else {
    correctnessValue = calculateEffectiveCorrectness({
      studentScore,
      response: response,
    });
  }

  const responseCellClass = propsToResponseClass(correctnessValue, showResults);
  const partialMarkPercentage = studentScore
    ? studentScore.score / gradingWeight
    : 0;
  const showAll = showResults && showResponses;

  const responseBlockClass = cx(
    'relative flex items-center justify-center w-full h-full px-4 py-2 m-0',
    'font-sans antialiased rounded-4px border-0 appearance-none outline-none select-none',
    'transition ease-in-out duration-150',
    responseCellClass.background,
    canEditResponse && ['group cursor-pointer', responseCellClass.hover]
  );
  const responseTextClass = cx(
    'relative block h-8 text-sm leading-8 truncate',
    responseCellClass.text,
    showAll ? 'ml-1' : ''
  );
  const responseIconClass = cx('relative flex', responseCellClass.text);

  return (
    <Box
      tagName={canEditResponse ? 'button' : 'div'}
      className={responseBlockClass}
      onClick={canEditResponse ? onSelectResponse : null}
    >
      {canEditResponse ? <FocusIndicator notOffset isInset /> : null}
      {showResults ? (
        <>
          {correctnessValue === Correctness.Partial ? (
            <div className="absolute inset-0 rounded-4px overflow-hidden">
              <div
                className="absolute inset-0 bg-amber-400 group-hocus:bg-amber-500 origin-left transition ease-in-out duration-150"
                style={{ transform: `scaleX(${partialMarkPercentage})` }}
              />
            </div>
          ) : null}
          <div className={responseIconClass}>
            <ResponseIcon correctness={correctnessValue} />
          </div>
        </>
      ) : null}
      {showResponses ? (
        <span className={responseTextClass}>{answerText}</span>
      ) : null}
      {/* Pencil icon for Short Answer if present (when responses or results are shown) */}
      {(showResponses || showResults) && isShortAnswer && answerText?.length > 0 ? (
        <span className={responseIconClass} aria-hidden>
          <IconResultsGridPencil title="Manually Grade" titleId="manually-grade" />
        </span>
      ) : null}
      {hasGradeOverride ? (
        <div className="absolute top-0 right-0 z-10 flex">
          <ResultsCellDogEar fillClass={responseCellClass.fold} />
        </div>
      ) : null}
    </Box>
  );
}
